import { isObservableArray, toJS } from 'mobx';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import snakeCase from 'lodash/snakeCase';

/**
 * Generate an updated array for nested attributes (rails format for has_many)
 * this will handle adding and/or removing relations
 *
 * @param {Object[]} oldDataArray - data before the change
 * @param {Object[]} newDataArray - data after the change
 * @param {Object[]} relationDataArray - relation data array before the change
 * @param {string} relationPropName - relation prop name, relationDataArray.relationPropName
 * this is required to find the relation of an object.
 * @param {string} [propName] - optional: if not provided will deduce it from relationPropName
 * this is required to create a new relation.
 *
 * @return {Object[]} result array of nested attributes in rails format.
 *
 * Example:
 * oldDataArray = [{id: 1, name: 'name1'}, {id: 2, name: 'name2'}] // array of user
 * newDataArray = [{id: 3, name: 'name3'}, {id: 2, name: 'name2'}] // array of user
 * relationDataArray = [{id: 1, userId: 1, cardId: 5 }, {id: 2, userId: 2, cardId: 5}] // relation between user and card
 * relationPropName = 'userId' // find relation based on this prop, user.id === relationDataArray[].userId
 * propName = 'userId' // similar but used to create the new relation
 *
 * this will return an array with relations that instruct to remove the relation for the user with id == 1 and
 * add new relation for the user with id == 3
 */


const genUpdatedNestedAttributes = (oldDataArray, relationDataArray, newDataArray, relationPropName, propName = relationPropName) => {
  // if its observable array, convert it to plain JS array
  if (isObservableArray(oldDataArray)) {
    oldDataArray = toJS(oldDataArray);
  }

  if (isObservableArray(newDataArray)) {
    newDataArray = toJS(newDataArray);
  }

  propName = snakeCase(propName);

  const toAdd = differenceWith(newDataArray, oldDataArray, isEqual);
  const toRemove = differenceWith(oldDataArray, newDataArray, isEqual);

  let attributes = toAdd.map((object) => ({ [propName]: object.id }));

  try {
    attributes = attributes.concat(toRemove.map((object) => ({
      id: _findRelationObject(object, relationDataArray, relationPropName).id,
      _destroy: true,
    })));
  } catch (e) {
    // nothing
  }

  // console.log(attributes);
  return attributes;
};


const getDifferentObjects = (allDataArray, selectedDataArray, comparator = isEqual) => {
  // if its observable array, convert it to plain JS array
  if (isObservableArray(allDataArray)) {
    allDataArray = toJS(allDataArray);
  }

  if (isObservableArray(selectedDataArray)) {
    selectedDataArray = toJS(selectedDataArray);
  }

  return differenceWith(allDataArray, selectedDataArray, comparator);
};


const _findRelationObject = (object, relationDataArray, relationPropName) => relationDataArray.find((relation) => relation[relationPropName] === object.id);


export { genUpdatedNestedAttributes, getDifferentObjects };
